import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { listingsApi, bookingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import ListingCard from '../../components/ListingCard';
import { 
  Building, Calendar, DollarSign, Plus, ArrowRight, 
  TrendingUp, Clock, Loader2 
} from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/utils';

export default function HostDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listingsRes, bookingsRes] = await Promise.all([
        listingsApi.getHostListings(),
        bookingsApi.getAll(),
      ]);
      setListings(listingsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingRequests = bookings.filter((b) => b.status === 'pending');
  const activeBookings = bookings.filter((b) => b.status === 'accepted');
  const totalEarnings = bookings
    .filter((b) => b.status === 'completed')
    .reduce((sum, b) => sum + b.total_amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="host-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-farmhouse-muted mt-1">
              Manage your listings and bookings
            </p>
          </div>
          <Link to="/host/listings/new">
            <Button className="btn-primary" data-testid="create-listing-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-saddle/20">
                <Building className="w-5 h-5 text-farmhouse-saddle" />
              </div>
            </div>
            <p className="metric-value">{listings.length}</p>
            <p className="metric-label">Total Listings</p>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-gold/20">
                <Clock className="w-5 h-5 text-farmhouse-gold" />
              </div>
            </div>
            <p className="metric-value">{pendingRequests.length}</p>
            <p className="metric-label">Pending Requests</p>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-moss/20">
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <p className="metric-value">{activeBookings.length}</p>
            <p className="metric-label">Active Bookings</p>
          </div>

          <div className="metric-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-md bg-farmhouse-saddle/20">
                <DollarSign className="w-5 h-5 text-farmhouse-saddle" />
              </div>
            </div>
            <p className="metric-value">{formatCurrency(totalEarnings)}</p>
            <p className="metric-label">Total Earnings</p>
          </div>
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
                Pending Requests
              </h2>
              <Link to="/host/requests" className="text-farmhouse-saddle hover:underline text-sm">
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {pendingRequests.slice(0, 3).map((booking) => (
                <div
                  key={booking.id}
                  className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-4 flex items-center justify-between"
                  data-testid={`pending-request-${booking.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="avatar bg-farmhouse-saddle text-white">
                      {booking.guest?.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-farmhouse-cream">{booking.guest?.name}</p>
                      <p className="text-sm text-farmhouse-muted">
                        {booking.listing?.title} • {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-farmhouse-cream">
                      {formatCurrency(booking.total_amount)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/host/requests?booking=${booking.id}`)}
                      className="btn-primary"
                    >
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Listings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-semibold text-farmhouse-cream">
              My Listings
            </h2>
            <Link to="/host/listings" className="text-farmhouse-saddle hover:underline text-sm">
              Manage All
            </Link>
          </div>

          {listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.slice(0, 6).map((listing) => (
                <ListingCard key={listing.id} listing={listing} showStatus />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
              <Building className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
              <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                No listings yet
              </h3>
              <p className="text-farmhouse-muted mb-4">
                Create your first listing to start hosting
              </p>
              <Link to="/host/listings/new">
                <Button className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Listing
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
